import { useMemo, useState } from "react";
import { Plus, RotateCcw } from "lucide-react";
import { t } from "../../i18n";
import { HostChip } from "../HostChip";

export type OrganizationNetworkPolicy = {
  siteOutboundHosts: string[];
  siteProviderHosts: string[];
  outboundHosts: string[] | null;
  providerHosts: string[] | null;
  effectiveOutboundHosts: string[] | null;
  effectiveProviderHosts: string[];
};

export function OrganizationNetworkPolicyEditor({
  policy,
  saving,
  canEdit = true,
  onReset,
  onChange,
  onSave,
}: {
  policy: OrganizationNetworkPolicy;
  saving: boolean;
  canEdit?: boolean;
  onReset: () => void;
  onChange: (policy: OrganizationNetworkPolicy) => void;
  onSave: () => void;
}) {
  const [outboundQuery, setOutboundQuery] = useState("");
  const [providerQuery, setProviderQuery] = useState("");
  const [outboundCandidate, setOutboundCandidate] = useState("");
  const [providerCandidate, setProviderCandidate] = useState("");
  const selectedOutbound = policy.outboundHosts ?? policy.siteOutboundHosts;
  const selectedProviders = policy.providerHosts ?? policy.siteProviderHosts;
  const outboundOptions = useMemo(
    () =>
      policy.siteOutboundHosts.filter(
        (host) =>
          !selectedOutbound.includes(host) &&
          host.toLowerCase().includes(outboundQuery.trim().toLowerCase()),
      ),
    [outboundQuery, policy.siteOutboundHosts, selectedOutbound],
  );
  const providerOptions = useMemo(
    () =>
      policy.siteProviderHosts.filter(
        (host) =>
          selectedOutbound.includes(host) &&
          !selectedProviders.includes(host) &&
          host.toLowerCase().includes(providerQuery.trim().toLowerCase()),
      ),
    [
      policy.siteProviderHosts,
      providerQuery,
      selectedOutbound,
      selectedProviders,
    ],
  );
  const removeOutbound = (host: string) => {
    const nextOutbound = selectedOutbound.filter((item) => item !== host);
    onChange({
      ...policy,
      outboundHosts: nextOutbound,
      providerHosts:
        policy.providerHosts === null
          ? null
          : selectedProviders.filter((item) => nextOutbound.includes(item)),
    });
  };
  const addOutbound = () => {
    if (!outboundCandidate) return;
    onChange({
      ...policy,
      outboundHosts: [...selectedOutbound, outboundCandidate],
    });
    setOutboundCandidate("");
  };
  const removeProvider = (host: string) =>
    onChange({
      ...policy,
      providerHosts: selectedProviders.filter((item) => item !== host),
    });
  const addProvider = () => {
    if (!providerCandidate) return;
    onChange({
      ...policy,
      providerHosts: [...selectedProviders, providerCandidate],
    });
    setProviderCandidate("");
  };
  const custom = policy.outboundHosts !== null;

  return (
    <section className="panel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold">{t("Outbound network policy")}</h2>
          <p className="mt-1 max-w-3xl text-sm text-stone-500">
            {t(
              canEdit
                ? "Build this organization's approved destination policy from the site catalog. Private networks and arbitrary hosts remain blocked."
                : "This policy is controlled by site permissions. The organization can use only the approved destinations shown below.",
            )}
          </p>
        </div>
        <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600 dark:bg-white/10 dark:text-stone-300">
          {custom
            ? t("Custom organization policy")
            : t("Inherited from site catalog")}
        </span>
      </div>

      <div className="mt-5 rounded-xl border border-stone-200 p-4 dark:border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold">{t("Allowed destinations")}</h3>
            <p className="text-xs text-stone-500">
              {t("site-approved hosts selected", {
                count: selectedOutbound.length,
                total: policy.siteOutboundHosts.length,
              })}
            </p>
          </div>
          {canEdit && custom && (
            <button
              className="button text-xs"
              disabled={saving}
              onClick={onReset}
            >
              <RotateCcw size={13} /> {t("Reset to site catalog")}
            </button>
          )}
        </div>
        <div className="mt-3 flex min-h-12 flex-wrap gap-2 rounded-lg bg-stone-50 p-3 dark:bg-white/4">
          {selectedOutbound.map((host) => (
            <HostChip
              key={host}
              host={host}
              canRemove={canEdit}
              onRemove={() => removeOutbound(host)}
            />
          ))}
          {!selectedOutbound.length && (
            <span className="self-center text-xs text-amber-700 dark:text-amber-300">
              {t("No outbound destinations selected")}
            </span>
          )}
        </div>
        {canEdit && (
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              className="field min-w-48 flex-1"
              aria-label={t("Filter approved destinations")}
              placeholder={t("Filter approved destinations")}
              value={outboundQuery}
              onChange={(event) => setOutboundQuery(event.target.value)}
            />
            <select
              className="field min-w-56 flex-1"
              value={outboundCandidate}
              onChange={(event) => setOutboundCandidate(event.target.value)}
            >
              <option value="">{t("Choose a site-approved host")}</option>
              {outboundOptions.map((host) => (
                <option key={host} value={host}>
                  {host}
                </option>
              ))}
            </select>
            <button
              className="button"
              disabled={saving || !outboundCandidate}
              onClick={addOutbound}
            >
              <Plus size={14} /> {t("Add destination")}
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 rounded-xl border border-stone-200 p-4 dark:border-white/10">
        <div>
          <h3 className="font-semibold">{t("Provider destinations")}</h3>
          <p className="text-xs text-stone-500">
            {t(
              "Optional narrower destinations for provider discovery and model requests.",
            )}
          </p>
        </div>
        <div className="mt-3 flex min-h-12 flex-wrap gap-2 rounded-lg bg-stone-50 p-3 dark:bg-white/4">
          {selectedProviders.map((host) => (
            <HostChip
              key={host}
              host={host}
              canRemove={canEdit}
              onRemove={() => removeProvider(host)}
            />
          ))}
          {!selectedProviders.length && (
            <span className="self-center text-xs text-stone-500">
              {t("No narrower provider policy")}
            </span>
          )}
        </div>
        {canEdit && (
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              className="field min-w-48 flex-1"
              aria-label={t("Filter provider destinations")}
              placeholder={t("Filter provider destinations")}
              value={providerQuery}
              onChange={(event) => setProviderQuery(event.target.value)}
            />
            <select
              className="field min-w-56 flex-1"
              value={providerCandidate}
              onChange={(event) => setProviderCandidate(event.target.value)}
            >
              <option value="">{t("Choose a provider host")}</option>
              {providerOptions.map((host) => (
                <option key={host} value={host}>
                  {host}
                </option>
              ))}
            </select>
            <button
              className="button"
              disabled={saving || !providerCandidate}
              onClick={addProvider}
            >
              <Plus size={14} /> {t("Add provider")}
            </button>
          </div>
        )}
      </div>

      {canEdit && (
        <div className="mt-5 flex justify-end">
          <button className="button-primary" disabled={saving} onClick={onSave}>
            {saving ? t("Saving…") : t("Save network policy")}
          </button>
        </div>
      )}
    </section>
  );
}
